import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { TenderingBasicInfo } from '../../../models/tendering-basicinfo.model';

import { AppConfig } from '../../../../app.config';
import { HttpClient } from '@angular/common/http';
import { catchError, map } from 'rxjs/operators';




@Injectable({
    providedIn: 'root'
})
export class TenderingBasicinfoService {

    private readonly config = inject(AppConfig);

    private urlIwmBackend: string = this.config.backendIwmUri + 'TenderingApi/';
    private urlCustomerAdmin: string = this.config.backendIwmUri + 'CustomerAdminApi/';

    constructor(private http: HttpClient) { }


    /**
      * Get tendering details by GUID
      * @param {string} id GUID tendering identifier
      * @returns {Observable<TenderingBasicInfo>}
      */
    public getTenderingBasicinfo(id: string): Observable<TenderingBasicInfo> {
        const url = this.urlIwmBackend + `Tenderings(${id})`;
        const tendering = this.http.get<TenderingBasicInfo>(url);
        return tendering;
    }

    /**
     * Get tendering details by iwmId
     * @param {number} iwmId numeric tendering identifier
     * @returns {Observable<TenderingBasicInfo>}
     */
    public getTenderingBasicInfoByIwmId(iwmId: number): Observable<TenderingBasicInfo> {
        const url = this.urlIwmBackend + `Tenderings?$filter=IwmId eq ${iwmId}`;
        const data = this.http.get<TenderingBasicInfo>(url);
        return data;
    }

    /**
      * Update tendering basicinformation
      * @param {string} tenderingId Id of the tendering
      * @param {TenderingBasicInfo} tenderingBasicInfo TenderingBasicInfo object
      * @returns {Observable<boolean>}
      */
    public patch(tenderingId: string, basicInfo: TenderingBasicInfo): Observable<any> {
        const url = this.urlIwmBackend + `Tenderings(${tenderingId})`;
        return this.http.patch(url, basicInfo);
    }

    public getTenderingSubtypes(): Observable<any> {
        const url = this.urlCustomerAdmin + `ListItems?$filter=Type eq 'TenderingSubType'`;
        const data = this.http.get<any>(url);
        return data;
    }

    public getTenderingListItemsByType(type: string): Observable<any> {
        const url = this.urlCustomerAdmin + `ListItems?$filter=Type eq '${type}'`;
        const data = this.http.get<any>(url);
        return data;
    }

    /**
   * Archive tendering (and catch possible error)
   * @param {string} tenderingId TenderingId
   */
    public archive(tenderingId: string): Observable<any> {
        const url = this.urlIwmBackend + `Tenderings(${tenderingId})/Default.Archive`;
        const resp = this.http.post(url, {})
            .pipe(catchError(e => of(e)));
        return resp;
    }

    /**
    * Batch archive tenderings
    * @param {Array<string>} tenderingIds array of tendering ids
    */
    public batchArchiveTenderings(tenderingIds: Array<string>): Observable<Array<string>> {
        const url = this.urlIwmBackend + 'Tenderings/Default.BatchArchive';
        return this.http.post<Array<string>>(url, { Tenderings: tenderingIds })
            .pipe(map((x: any) => x?.value));
    }

    /**
     * Restore tendering (and catch possible error)
     * @param {string} tenderingId TenderingId
     */
    public restore(tenderingId: string): Observable<any> {
        const url = this.urlIwmBackend + `Tenderings(${tenderingId})/Default.Restore`;
        const resp = this.http.post(url, {})
            .pipe(catchError(e => of(e)));
        return resp;
    }

    /**
    * Batch restore invoicing proposals from archive
    * @param {Array<string>} tenderingIds array of tendering ids
    */
    public batchRestoreTenderings(tenderingIds: Array<string>): Observable<Array<string>> {
        const url = this.urlIwmBackend + 'Tenderings/Default.BatchRestore';
        return this.http.post<Array<string>>(url, { Tenderings: tenderingIds })
            .pipe(map((x: any) => x?.value));
    }

    /**
   * Add new priceComponent
   * @param {string} tenderingId TenderingId
   * @param {string} value value (name) of the price component to add
   */
    public addPriceComponent(tenderingId: string, value: string): Observable<any> {
        const url = this.urlIwmBackend + `Tenderings(${tenderingId})/Default.AddPriceComponent`;
        return this.http.post(url, { Value: value });
    }

    /**
  * Remove priceComponent
  * @param {string} priceComponentId Id of the price component to remove
  */
    public removePriceComponent(priceComponentId: string): Observable<any> {
        const url = this.urlIwmBackend + `Tenderings/Default.RemovePriceComponent`;
        return this.http.post(url, { PriceComponentId: priceComponentId });
    }

    /**
     * Complete tendering
     * @param {string} tenderingId TenderingId
     */
    public complete(tenderingId: string): Observable<TenderingBasicInfo> {
        const url = this.urlIwmBackend + `Tenderings(${tenderingId})/Default.Complete`;
        return this.http.post<TenderingBasicInfo>(url, {})
    }
}
